import React, { useEffect, useRef } from 'react';
import { FiBold, FiItalic, FiUnderline, FiLink, FiList } from 'react-icons/fi';

/**
 * Minimal WYSIWYG for broadcast bodies.
 *
 * Built on document.execCommand rather than a rich-text library: the app has no
 * editor dependency, the allowed markup is a short allowlist the server enforces
 * anyway, and adding an editor bundle for five toolbar buttons is not worth it.
 * Pasting is forced to plain text so foreign styles can never reach the editor —
 * the sanitiser would strip them on save, and showing formatting that then
 * disappears would be worse than not showing it.
 */

function escapeText(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

const TOOLS = [
    { command: 'bold', icon: FiBold, label: 'Bold' },
    { command: 'italic', icon: FiItalic, label: 'Italic' },
    { command: 'underline', icon: FiUnderline, label: 'Underline' },
    { command: 'insertUnorderedList', icon: FiList, label: 'Bullet list' }
];

export default function RichTextEditor({ value, onChange, placeholder = 'Write the announcement…' }) {
    const ref = useRef(null);

    // Only push the value down when it differs from the DOM, otherwise every
    // keystroke re-renders and the caret jumps to the start.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (el.innerHTML !== (value || '')) el.innerHTML = value || '';
    }, [value]);

    const sync = () => {
        const el = ref.current;
        if (el) onChange(el.innerHTML);
    };

    const exec = (command, argument) => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        try {
            document.execCommand(command, false, argument);
        } catch (e) {
            /* command unsupported in this browser — the toolbar button no-ops */
        }
        sync();
    };

    const insertLink = () => {
        const raw = window.prompt('Link URL', 'https://');
        if (!raw) return;
        const url = raw.trim();
        if (!/^https?:\/\//i.test(url)) {
            window.alert('Only http:// and https:// links are allowed — the popup opens on a site we do not control.');
            return;
        }

        const selection = window.getSelection();
        // createLink needs a selection; with the caret only, insert the URL as
        // its own link instead of silently doing nothing.
        if (!selection || selection.isCollapsed) {
            exec('insertHTML', `<a href="${escapeAttr(url)}">${escapeText(url)}</a>`);
        } else {
            exec('createLink', url);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
        exec('insertHTML', escapeText(text).replace(/\r?\n/g, '<br>'));
    };

    return (
        <div className="rounded-md border border-input bg-background">
            <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
                {TOOLS.map(tool => (
                    <button
                        key={tool.command}
                        type="button"
                        title={tool.label}
                        aria-label={tool.label}
                        // mousedown would otherwise move focus out of the editor
                        // and collapse the selection the command needs.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => exec(tool.command)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                    >
                        <tool.icon size={15} />
                    </button>
                ))}
                <span className="w-px h-4 bg-border mx-1" />
                <button
                    type="button"
                    title="Link"
                    aria-label="Link"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={insertLink}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                >
                    <FiLink size={15} />
                </button>
            </div>

            <div className="relative">
                {!value && (
                    <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-muted-foreground">
                        {placeholder}
                    </span>
                )}
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    aria-multiline="true"
                    onInput={sync}
                    onBlur={sync}
                    onPaste={handlePaste}
                    className="min-h-[140px] max-h-[320px] overflow-y-auto px-3 py-2.5 text-sm text-foreground outline-none [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold"
                />
            </div>
        </div>
    );
}
